/* eslint-disable react/no-array-index-key */
import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Skeleton,
  Divider,
  Tooltip,
  IconButton,
  useTheme,
  Fade,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  MeetingRoom as MeetingRoomIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  PlayCircle as PlayCircleIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarIcon,
  ErrorOutline as ErrorOutlineIcon,
  SmartToy as AIIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getUserDetails } from '../../utils';
import { useQueryGetAllMeetingsList } from '../../hooks/react-query/meetings';
import { useQueryGetSchoolsList } from '../../hooks/react-query/schools';
import CustomPagination from '../../components/pagination/CustomPagination';

const DEFAULT_PAGE_SIZE = 12;

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  ENDED: {
    label: 'Completed',
    color: 'success',
    icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
    gradient: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    bg: (theme) => alpha(theme.palette.success.main, 0.1),
    text: (theme) => theme.palette.success.dark,
  },
  PENDING: {
    label: 'Scheduled',
    color: 'warning',
    icon: <PendingIcon sx={{ fontSize: 16 }} />,
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    bg: (theme) => alpha(theme.palette.warning.main, 0.1),
    text: (theme) => theme.palette.warning.dark,
  },
  INPROGRESS: {
    label: 'In Progress',
    color: 'info',
    icon: <PlayCircleIcon sx={{ fontSize: 16 }} />,
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    bg: (theme) => alpha(theme.palette.info.main, 0.1),
    text: (theme) => theme.palette.info.dark,
  },
  // Backward compatibility for any direct STARTED status usage.
  STARTED: {
    label: 'In Progress',
    color: 'info',
    icon: <PlayCircleIcon sx={{ fontSize: 16 }} />,
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    bg: (theme) => alpha(theme.palette.info.main, 0.1),
    text: (theme) => theme.palette.info.dark,
  },
  INCOMPLETE: {
    label: 'Incomplete',
    color: 'info',
    icon: <AccessTimeIcon sx={{ fontSize: 16 }} />,
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    bg: (theme) => alpha(theme.palette.info.main, 0.1),
    text: (theme) => theme.palette.info.dark,
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'error',
    icon: <CancelIcon sx={{ fontSize: 16 }} />,
    gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    bg: (theme) => alpha(theme.palette.error.main, 0.1),
    text: (theme) => theme.palette.error.dark,
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getInitials = (fname, lname) =>
  `${fname?.charAt(0) ?? ''}${lname?.charAt(0) ?? ''}`.toUpperCase();

const getDisplayStatus = (meeting) => {
  const rawStatus = (meeting?.status || 'PENDING').toUpperCase();
  const slotEndTime = meeting?.slot ? new Date(meeting.slot) : null;
  const now = new Date();

  if (rawStatus === 'PENDING' && slotEndTime && !Number.isNaN(slotEndTime.getTime())) {
    return now < slotEndTime ? 'PENDING' : 'INCOMPLETE';
  }

  if (rawStatus === 'STARTED' && slotEndTime && !Number.isNaN(slotEndTime.getTime())) {
    return now > slotEndTime ? 'INCOMPLETE' : 'INPROGRESS';
  }

  return rawStatus;
};

// ── Summary stat card ─────────────────────────────────────────────────────────
function StatCard({ icon, label, value, gradient, color, tooltip }) {
  return (
    <Tooltip title={tooltip || ''} arrow placement="top">
      <span>
        <Card
          sx={{
            p: 2.5,
            borderRadius: 3,
            background: gradient,
            boxShadow: `0 4px 20px ${alpha(color, 0.25)}`,
            color: '#fff',
            height: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {icon}
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2, color: '#fff' }}>
                {value}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                {label}
              </Typography>
            </Box>
          </Box>
        </Card>
      </span>
    </Tooltip>
  );
}

// ── Meeting card ──────────────────────────────────────────────────────────────
function MeetingCard({ meeting }) {
  const theme = useTheme();
  const from = meeting?.inviteId?.from;
  const recipient = meeting?.inviteId?.recipient;
  const statusKey = getDisplayStatus(meeting);
  const cfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG.PENDING;
  const participantList = [from, recipient];
  const getTextOrNA = (value) => (value === null || value === undefined || value === '' ? 'N/A' : value);
  const aiPermissionLabel = meeting?.aiNotetakerPermission === true
    ? 'Allowed'
    : meeting?.aiNotetakerPermission === false
      ? 'Denied'
      : 'N/A';

  return (
    <Fade in timeout={400}>
      <Card
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: theme.customShadows?.card ?? '0 2px 8px rgba(0,0,0,0.08)',
          border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          transition: 'box-shadow 0.2s, transform 0.2s',
          height: '100%',
          '&:hover': {
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
            transform: 'translateY(-2px)',
          },
        }}
      >
        {/* Coloured status bar */}
        <Box sx={{ height: 4, background: cfg.gradient, mb: 2 }} />

        <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Status chip + date */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Chip
              icon={cfg.icon}
              label={cfg.label}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: '0.72rem',
                background: cfg.bg(theme),
                color: cfg.text(theme),
                border: `1px solid ${alpha(cfg.text(theme), 0.3)}`,
                '& .MuiChip-icon': { color: cfg.text(theme) },
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary">
                {formatDate(meeting?.slot)}
              </Typography>
            </Box>
          </Box>

          {/* Participants */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{ color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Participants
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1 }}>
              {participantList.map((user, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar
                    src={user?.image}
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: '0.7rem',
                      background:
                        idx === 0
                          ? 'linear-gradient(135deg, #A8469B 0%, #569cd3 100%)'
                          : 'linear-gradient(135deg, #569cd3 0%, #22c55e 100%)',
                    }}
                  >
                    {user ? getInitials(user.fname, user.lname) : 'NA'}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {user ? `${getTextOrNA(user.fname)} ${getTextOrNA(user.lname)}` : 'N/A'}
                      {user ? (
                        <Tooltip
                          title={
                            idx === 0
                              ? (meeting?.surveyStatus?.fromSubmitted ? 'Survey submitted' : 'Survey not submitted')
                              : (meeting?.surveyStatus?.recipientSubmitted ? 'Survey submitted' : 'Survey not submitted')
                          }
                          arrow
                          placement="top"
                        >
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            {(idx === 0 ? meeting?.surveyStatus?.fromSubmitted : meeting?.surveyStatus?.recipientSubmitted) ? (
                              <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                            ) : (
                              <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
                            )}
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">N/A</Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {user ? getTextOrNA(user.email) : 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 1.5, opacity: 0.5 }} />

          {/* Duration + Scheduled */}
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <AccessTimeIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Box>
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', lineHeight: 1 }}>
                    Duration
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDuration(meeting?.duration)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <CalendarIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Box>
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', lineHeight: 1 }}>
                    Scheduled
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDateTime(meeting?.slot)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* AI notetaker */}
          <>
            <Divider sx={{ my: 1.5, opacity: 0.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <AIIcon
                sx={{
                  fontSize: 16,
                  color: meeting?.aiNotetakerPermission === true ? 'success.main' : 'text.disabled',
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: meeting?.aiNotetakerPermission === true ? 'success.main' : 'text.secondary',
                }}
              >
                AI Notes: {aiPermissionLabel}
              </Typography>
              <Chip
                label={meeting?.aiNotetakerPermission === true && meeting?.aiSummary ? 'Summary Ready' : 'Summary: N/A'}
                size="small"
                color={meeting?.aiNotetakerPermission === true && meeting?.aiSummary ? 'success' : 'default'}
                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, ml: 'auto' }}
              />
            </Box>
          </>

          {/* Start / End times */}
          <>
            <Divider sx={{ my: 1.5, opacity: 0.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5, mt: 'auto' }}>
              <Typography variant="caption" color="text.secondary">
                Started: {formatDateTime(meeting?.startTime)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Ended: {formatDateTime(meeting?.endTime)}
              </Typography>
            </Box>
          </>
        </CardContent>
      </Card>
    </Fade>
  );
}

// ── Skeleton card ─────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <Skeleton variant="rectangular" height={4} />
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Skeleton variant="rounded" width={90} height={24} />
          <Skeleton variant="text" width={80} />
        </Box>
        <Skeleton variant="text" width="40%" sx={{ mb: 1 }} />
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          {[0, 1].map((i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Box>
                <Skeleton variant="text" width={80} />
                <Skeleton variant="text" width={120} />
              </Box>
            </Box>
          ))}
        </Box>
        <Skeleton variant="text" />
        <Skeleton variant="text" width="80%" />
      </CardContent>
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function MeetingDetails() {
  const navigate = useNavigate();
  const theme = useTheme();
  const userDetails = getUserDetails();

  const isAdmin = userDetails?.role?.toUpperCase() === 'ADMIN';
  // Send the first valid schoolId from the array (site leaders typically have one school)
  const rawSchoolIds = Array.isArray(userDetails?.schoolId)
    ? userDetails.schoolId.filter(Boolean).map(id => (typeof id === 'object' ? id._id || id.toString() : id))
    : [];
  const schoolId = isAdmin ? '' : (rawSchoolIds[0] || '');

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [selectedSchoolId, setSelectedSchoolId] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const effectiveSchoolId = isAdmin ? selectedSchoolId : schoolId;

  const { data: schoolsList } = useQueryGetSchoolsList(
    { token: userDetails?.token, role: userDetails?.role },
    toast
  );

  const { data: response, isLoading, isFetching, refetch } = useQueryGetAllMeetingsList(
    userDetails?.token,
    { status, search, page: page + 1, limit: rowsPerPage, schoolId: effectiveSchoolId },
    navigate,
    null
  );

  const responseData = response?.data?.data;
  const meetings = responseData?.meetings ?? [];
  const total = responseData?.total ?? 0;

  // Use backend-computed counts so stats reflect ALL filtered results, not just the current page
  const summaryStats = useMemo(() => ({
    ENDED: responseData?.statusCounts?.ENDED ?? 0,
    SCHEDULED: responseData?.timeCounts?.SCHEDULED ?? 0,
    INPROGRESS: responseData?.timeCounts?.INPROGRESS ?? 0,
    INCOMPLETE: responseData?.timeCounts?.INCOMPLETE ?? 0,
    CANCELLED: responseData?.statusCounts?.CANCELLED ?? 0,
  }), [responseData?.statusCounts, responseData?.timeCounts]);

  const handleSearchChange = (e) => { setSearch(e.target.value); setPage(0); };
  const handleStatusChange = (e) => { setStatus(e.target.value); setPage(0); };
  const handleSchoolChange = (e) => { setSelectedSchoolId(e.target.value); setPage(0); };
  const handleRowsPerPageChange = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

  const statCards = [
    {
      label: 'Total Sessions',
      value: total,
      tooltip: 'Shows how many sessions match your current filters.',
      icon: <MeetingRoomIcon sx={{ fontSize: 28, color: '#fff' }} />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#667eea',
    },
    {
      label: 'Completed',
      value: summaryStats.ENDED,
      tooltip: 'Sessions that have been completed.',
      icon: <CheckCircleIcon sx={{ fontSize: 28, color: '#fff' }} />,
      gradient: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
      color: '#22c55e',
    },
    {
      label: 'Scheduled',
      value: summaryStats.SCHEDULED,
      tooltip: 'Sessions that are planned for a future time.',
      icon: <PendingIcon sx={{ fontSize: 28, color: '#fff' }} />,
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      color: '#f59e0b',
    },
    {
      label: 'In Progress',
      value: summaryStats.INPROGRESS,
      tooltip: 'Sessions that are currently happening.',
      icon: <PlayCircleIcon sx={{ fontSize: 28, color: '#fff' }} />,
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      color: '#3b82f6',
    },
    {
      label: 'Incomplete',
      value: summaryStats.INCOMPLETE,
      tooltip: 'Sessions that were not completed in time.',
      icon: <ErrorOutlineIcon sx={{ fontSize: 28, color: '#fff' }} />,
      gradient: 'linear-gradient(135deg, #7c4dff 0%, #5e35b1 100%)',
      color: '#7c4dff',
    },
    {
      label: 'Cancelled',
      value: summaryStats.CANCELLED,
      tooltip: 'Sessions that were cancelled.',
      icon: <CancelIcon sx={{ fontSize: 28, color: '#fff' }} />,
      gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      color: '#ef4444',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Session Details | Edifying Teachers</title>
      </Helmet>

      <Container maxWidth="xl" sx={{ pt: 0, pb: 4 }}>
        {/* ── Page header ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              background: alpha(theme.palette.primary.main, 0.1),
              '&:hover': { background: alpha(theme.palette.primary.main, 0.2) },
            }}
          >
            <ArrowBackIcon sx={{ color: 'primary.main' }} />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: 'var(--gradient-primary, linear-gradient(135deg, #A8469B 0%, #569cd3 100%))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Session Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Browse all sessions with status, participants and duration
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => refetch()}
              disabled={isFetching}
              sx={{
                background: alpha(theme.palette.primary.main, 0.08),
                '&:hover': { background: alpha(theme.palette.primary.main, 0.16) },
              }}
            >
              <RefreshIcon
                sx={{
                  color: 'primary.main',
                  animation: isFetching ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    from: { transform: 'rotate(0deg)' },
                    to: { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── Stat cards ── */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {statCards.map((card) => (
            <Grid item xs={6} sm={4} md={2} key={card.label}>
              <StatCard {...card} />
            </Grid>
          ))}
        </Grid>

        {/* ── Filter bar ── */}
        <Card
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            boxShadow: 'none',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FilterIcon sx={{ color: 'text.secondary' }} />
            <TextField
              size="small"
              placeholder="Search by participant name or email…"
              value={search}
              onChange={handleSearchChange}
              sx={{ flex: 1, minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select value={status} label="Status" onChange={handleStatusChange}>
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="ENDED">Completed</MenuItem>
                <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                <MenuItem value="INPROGRESS">In Progress</MenuItem>
                <MenuItem value="INCOMPLETE">Incomplete</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </Select>
            </FormControl>
            {isAdmin && (
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="meeting-partnership-site-label">Partnership Site</InputLabel>
                <Select
                  labelId="meeting-partnership-site-label"
                  value={selectedSchoolId}
                  label="Partnership Site"
                  onChange={handleSchoolChange}
                >
                  <MenuItem value="">All Partnership Sites</MenuItem>
                  {schoolsList?.data?.data?.map((school) => (
                    <MenuItem key={school._id} value={school._id}>
                      {school.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', whiteSpace: 'nowrap' }}>
              {total} session{total !== 1 ? 's' : ''} found
            </Typography>
          </Box>
        </Card>

        {/* ── Cards grid ── */}
        {isLoading ? (
          <Grid container spacing={2.5}>
            {Array.from({ length: DEFAULT_PAGE_SIZE }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <SkeletonCard />
              </Grid>
            ))}
          </Grid>
        ) : meetings.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 10,
              color: 'text.secondary',
              background: alpha(theme.palette.background.paper, 0.6),
              borderRadius: 3,
              border: `1px dashed ${theme.palette.divider}`,
            }}
          >
            <MeetingRoomIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              No sessions found
            </Typography>
            <Typography variant="body2">Try adjusting your search or status filter.</Typography>
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            {meetings.map((meeting) => (
              <Grid item xs={12} sm={6} md={4} key={meeting._id}>
                <MeetingCard meeting={meeting} />
              </Grid>
            ))}
          </Grid>
        )}

        {/* ── Pagination ── */}
        {total > 0 && (
          <Box
            sx={{
              mt: 4,
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              background: theme.palette.background.paper,
              overflow: 'hidden',
            }}
          >
            <CustomPagination
              count={total}
              page={page}
              onPageChange={(_, val) => setPage(val)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[6, 12, 24, 48]}
              labelRowsPerPage="Sessions per page:"
              showRowsPerPage
              showPageInfo
              showTotalCount={false}
            />
          </Box>
        )}
      </Container>
    </>
  );
}
